import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { isNil } from 'ramda'
import styled from 'styled-components'
import { breakpoints } from '@/styles'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  PhoneLink,
  EmailLink,
  Wrapper,
  EmptyComponent,
  BasicSubTitle
} from '@/features/Application/elements'

const StyledLabeledValue = styled(LabeledValue)`
  grid-gap: 24px;
  grid-template-columns: calc(25% - 12px) 1fr;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: 1fr 1fr;
  }
`

const renderAddress = data => {
  const address = data.suite_number
    ? `${data.suite_number} - ${data.street_address}`
    : data.street_address
  return `${address} ${data.city} ${data.state} ${data.zip?.toUpperCase()}`
}

const LandlordReferencesView = ({ application, applicationLoading }) => {
  const { t } = useTranslation()

  const landlords = application.landlord_references ?? []
  const title = t('l.application.view.landlord_references.label', 'Landlord References')

  const data = useMemo(
    () =>
      landlords.map(landlord => ({
        subTitle: <BasicSubTitle user={landlord} />,
        items: [
          {
            id: 'Phone Number',
            cell: (
              <LabeledValue
                key="Phone Number"
                label={
                  <LabelText>
                    {t('l.application.view.phone_number.label', 'Phone Number')}
                  </LabelText>
                }
                value={
                  <PhoneLink
                    phoneNumber={landlord.phone_number}
                    countryCode={landlord.phone_country_code_alpha}
                  />
                }
              />
            )
          },
          {
            id: 'Email',
            cell: (
              <LabeledValue
                key="Email"
                label={<LabelText>{t('l.application.view.email.label', 'Email')}</LabelText>}
                value={!isNil(landlord.email) ? <EmailLink email={landlord.email} /> : '-'}
              />
            )
          },
          {
            id: 'Start Date',
            cell: (
              <LabeledValue
                key="Start Date"
                label={
                  <LabelText>{t('l.application.view.start_date.label', 'Start Date')}</LabelText>
                }
                value={
                  <ValueText>
                    {landlord?.extra_info?.start_date
                      ? moment(landlord?.extra_info?.start_date).format('YYYY-MM-DD')
                      : '-'}
                  </ValueText>
                }
              />
            )
          },
          {
            id: 'End Date',
            cell: (
              <LabeledValue
                key="End Date"
                label={<LabelText>{t('l.application.view.end_date.label', 'End Date')}</LabelText>}
                value={
                  <ValueText>
                    {landlord?.extra_info?.end_date
                      ? moment(landlord?.extra_info?.end_date).format('YYYY-MM-DD')
                      : t('r.profile.view.current_date.text', 'Current')}
                  </ValueText>
                }
              />
            )
          },
          {
            id: 'Current Address',
            cell: (
              <StyledLabeledValue
                key="Current Address"
                label={
                  <LabelText>
                    {t('l.application.view.current_address.label', 'Current Address')}
                  </LabelText>
                }
                value={
                  <ValueText>
                    {landlord.extra_info?.street_address ? renderAddress(landlord.extra_info) : '-'}
                  </ValueText>
                }
                gridColumns="1 / 3"
              />
            )
          },
          {
            id: 'Reason for leaving',
            cell: (
              <StyledLabeledValue
                key="Reason for leaving"
                label={
                  <LabelText>
                    {t('l.application.view.reason_for_leaving.label', 'Reason for leaving')}
                  </LabelText>
                }
                value={
                  <ValueText>
                    {landlord.extra_info?.reason_for_leaving
                      ? landlord.extra_info?.reason_for_leaving
                      : '-'}
                  </ValueText>
                }
                gridColumns="1 / 3"
              />
            )
          }
        ]
      })),
    [applicationLoading]
  )

  return (
    <Wrapper>
      <DisplayTable
        data={data}
        title={title}
        columns={1}
        subColumns={2}
        emptyComponent={
          <EmptyComponent isLoading={applicationLoading}>
            {t(
              'l.application.view.no_landlord.label',
              'No landlord references included in this application.'
            )}
          </EmptyComponent>
        }
      />
    </Wrapper>
  )
}

export default LandlordReferencesView
